/**
 * Cookie consent implementation for GDPR compliance
 * Handles user preferences for analytics tracking
 */

export default class Consent {
  constructor() {
    this.consentKey = 'analytics-consent';
    this.consentBannerId = 'cookieConsent';
    this.acceptButtonId = 'cookieConsentAccept';
    this.declineButtonId = 'cookieConsentDecline';
  }

  /**
   * Initialize the consent module
   */
  init() {
    console.log("[Consent Module] Initializing");
    this.setupConsentUI();
  }

  /**
   * Set up the consent UI and handle user interactions
   */
  setupConsentUI() {
    console.log("[Consent Module] Setting up UI");
    const analyticsConsent = this.getStoredConsent();
    
    const cookieConsent = document.getElementById(this.consentBannerId);
    
    console.log("[Consent Module] Consent status:", analyticsConsent);
    console.log("[Consent Module] Consent element found:", !!cookieConsent);
    
    if (!cookieConsent) {
      console.warn('[Consent Module] Cookie consent element not found');
      return;
    }
    
    // Show consent modal if no preference is stored
    if (analyticsConsent === null) {
      console.log("[Consent Module] No consent stored, showing modal");
      
      // Use requestAnimationFrame to show the modal after critical content is loaded
      requestAnimationFrame(() => {
        // Show the modal after a small delay to ensure page is loaded
        setTimeout(() => {
          cookieConsent.style.display = 'block';
          
          // Allow scrolling but prevent interaction (handled by overlay)
          // This way users can see the site but need to make a choice
          document.body.style.overflow = 'auto'; 
          
          console.log("[Consent Module] Modal now displayed");
        }, 500);
      });
      
      // Only bind button handlers when consent is needed
      this.bindConsentButtons();
    } else {
      console.log("[Consent Module] Consent already stored:", analyticsConsent);
      cookieConsent.style.display = 'none';
    }
  }

  /**
   * Bind consent button event handlers
   */
  bindConsentButtons() {
    // Accept button handler
    const acceptButton = document.getElementById(this.acceptButtonId);
    if (acceptButton) {
      console.log("[Consent Module] Accept button found, binding handler");
      acceptButton.addEventListener('click', () => {
        console.log("[Consent Module] Accept button clicked");
        this.setStoredConsent('accepted');
        const cookieConsent = document.getElementById(this.consentBannerId);
        if (cookieConsent) {
          cookieConsent.style.display = 'none';
        }
        
        // Dispatch consent event
        console.log("[Consent Module] Dispatching consent accepted event");
        document.dispatchEvent(new CustomEvent('analytics:consent', {
          detail: { accepted: true }
        }));
      });
    } else {
      console.warn("[Consent Module] Accept button NOT found");
    }
    
    // Decline button handler
    const declineButton = document.getElementById(this.declineButtonId);
    if (declineButton) {
      console.log("[Consent Module] Decline button found, binding handler");
      declineButton.addEventListener('click', () => {
        console.log("[Consent Module] Decline button clicked");
        this.setStoredConsent('declined');
        const cookieConsent = document.getElementById(this.consentBannerId);
        if (cookieConsent) {
          cookieConsent.style.display = 'none';
        }
        
        // Dispatch consent event
        console.log("[Consent Module] Dispatching consent declined event");
        document.dispatchEvent(new CustomEvent('analytics:consent', {
          detail: { accepted: false }
        }));
      });
    } else {
      console.warn("[Consent Module] Decline button NOT found");
    }
  }

  /**
   * Safely get stored consent with fallback
   */
  getStoredConsent() {
    try {
      return localStorage.getItem(this.consentKey);
    } catch (error) {
      console.warn('[Consent Module] localStorage not available, consent cannot be persisted');
      return null;
    }
  }

  /**
   * Safely store consent with fallback
   */
  setStoredConsent(value) {
    try {
      localStorage.setItem(this.consentKey, value);
      return true;
    } catch (error) {
      console.warn('[Consent Module] localStorage not available, consent cannot be persisted');
      return false;
    }
  }
  
  /**
   * Reset consent and show banner (for testing purposes)
   */
  resetConsent() {
    console.log("[Consent Module] Resetting consent for testing");
    try {
      localStorage.removeItem(this.consentKey);
    } catch (error) {
      console.warn('[Consent Module] localStorage not available for reset');
    }
    this.setupConsentUI();
    return "Consent reset. Banner should appear if page is loaded.";
  }
  
  /**
   * Simulate accepting consent (for testing purposes)
   */
  simulateAccept() {
    console.log("[Consent Module] Simulating consent acceptance");
    this.setStoredConsent('accepted');
    const cookieConsent = document.getElementById(this.consentBannerId);
    if (cookieConsent) {
      cookieConsent.style.display = 'none';
    }
    document.dispatchEvent(new CustomEvent('analytics:consent', {
      detail: { accepted: true }
    }));
    return "Consent simulated as accepted.";
  }
  
  /**
   * Simulate declining consent (for testing purposes)
   */
  simulateDecline() {
    console.log("[Consent Module] Simulating consent decline");
    this.setStoredConsent('declined');
    const cookieConsent = document.getElementById(this.consentBannerId);
    if (cookieConsent) {
      cookieConsent.style.display = 'none';
    }
    document.dispatchEvent(new CustomEvent('analytics:consent', {
      detail: { accepted: false }
    }));
    return "Consent simulated as declined.";
  }
}
